import { createStore, useStore } from 'zustand';

/**
 * The cancer type an Ask conversation has worked out, carried into the General
 * Inquiry composer when the reader starts an inquiry from it.
 *
 * Set by the handoff right before it navigates, from the snapshot in the Query
 * cache at that moment rather than a copy taken when the conversation opened,
 * because the server re-derives it every turn. Cleared by the composer on
 * unmount so it cannot prefill a later, unrelated inquiry.
 *
 * `id` is the inquiry cancer type id; when it is null the server matched no
 * visible cancer type and `label` is only a hint for the reader.
 */
export type InquiryCancerTypePrefill = {
  conversationId: string;
  id: number | null;
  label: string | null;
};

type InquiryCancerTypePrefillState = {
  prefill: InquiryCancerTypePrefill | null;
  setPrefill: (prefill: InquiryCancerTypePrefill | null) => void;
};

export const inquiryCancerTypePrefillStore = createStore<InquiryCancerTypePrefillState>((set) => ({
  prefill: null,
  setPrefill: (prefill) => set({ prefill }),
}));

export function setInquiryCancerTypePrefill(prefill: InquiryCancerTypePrefill | null): void {
  inquiryCancerTypePrefillStore.getState().setPrefill(prefill);
}

export function useInquiryCancerTypePrefill(): InquiryCancerTypePrefill | null {
  return useStore(inquiryCancerTypePrefillStore, (state) => state.prefill);
}

/**
 * Splits a prefill into what the form can use: a cancer type id it offers, or
 * failing that a label to show as a hint. The server's id can name a cancer
 * type the app's list does not carry, which is treated like no id at all.
 * Returns nothing until the list has loaded, so a label never flashes as a
 * hint ahead of the id it is about to be replaced by.
 */
export function resolveInquiryCancerTypePrefill(
  prefill: InquiryCancerTypePrefill | null,
  offeredIds: ReadonlyMap<number, unknown> | undefined
): { cancerFocusId: string; hint: string | undefined } {
  if (!prefill || !offeredIds) {
    return { cancerFocusId: '', hint: undefined };
  }

  if (prefill.id && offeredIds.has(prefill.id)) {
    return { cancerFocusId: String(prefill.id), hint: undefined };
  }

  return { cancerFocusId: '', hint: prefill.label || undefined };
}
